import { useCallback, useEffect, useState } from "react";

import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import LazySearchSelect from "../../components/shared/LazySearchSelect";
import PageHeader from "../../components/shared/PageHeader";
import Pagination from "../../components/shared/Pagination";
import SearchInput from "../../components/shared/SearchInput";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useApi } from "../../context/ApiContext";
import { useAuth } from "../../context/AuthContext";
import { useConfirm } from "../../context/ConfirmContext.jsx";
import { usePagination } from "../../hooks/usePagination";

import PermissionTable from "./component/PermissionTable";
import RoleTable from "./component/RoleTable";

const SCREENS = [
  { id: "dashboard", label: "Dashboard" },
  { id: "project", label: "Projects" },
  { id: "milestone", label: "Milestones" },
  { id: "task", label: "Tasks" },
  { id: "activity", label: "Activities" },
  { id: "material", label: "Materials" },
  { id: "attendance", label: "Attendance" },
  { id: "document", label: "Documents" },
  { id: "meeting", label: "Meetings" },
  { id: "lead", label: "Lead Manager" },
  { id: "vendor", label: "Vendors" },
  { id: "contractor", label: "Contractors" },
  { id: "costing", label: "Costing" },
  { id: "invoice", label: "Invoices" },
  { id: "payment", label: "Payments" },
  { id: "notes", label: "Notes" },
  { id: "user", label: "User Management" },
  { id: "role", label: "Role Management" },
  { id: "cost", label: "Cost & Budget Data" }
];

const getSingleScreenDefault = () => ({
  create: false,
  read: false,
  update: false,
  delete: false,
  approve: false
});

const RoleManagement = () => {
  const { user } = useAuth();
  const api = useApi();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleName, setRoleName] = useState("");
  const [isPermDialogOpen, setIsPermDialogOpen] = useState(false);
  const [permRole, setPermRole] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const isSuperAdmin = user?.role === "super_admin" || user?.role?.name === "super_admin";

  const pagination = usePagination(10);
  const { items: roles, currentPage: page, pageSize, setPaginationData, setCurrentPage: setPage } = pagination;

  // Filter out current user's role from the UI as well
  const filteredRoles = roles.filter((r) => {
    if (isSuperAdmin) return true;
    if (user?.role?.id && r.id === user.role.id) return false;
    // Fallback if role is just a string or object name
    if (typeof user?.role === "string" && r.name === user.role) return false;
    if (user?.role?.name && r.name === user.role.name) return false;
    return true;
  });

  const [screensList, setScreensList] = useState(SCREENS);

  useEffect(() => {
    const fetchSchema = async () => {
      try {
        const response = await api.get("/roles/schema");
        if (response.data && Array.isArray(response.data)) {
          setScreensList(response.data);
        }
      } catch (err) {
        console.error("Failed to load permissions schema", err);
      }
    };
    fetchSchema();
  }, [api]);

  const availableScreens = isSuperAdmin ? screensList : screensList.filter((screen) => user?.permissions?.[screen.id]?.read);

  const loadRoles = useCallback(async () => {
    try {
      const response = await api.get(`/roles`, {
        params: {
          page,
          size: pageSize,
          search: searchTerm.trim() || undefined,
          showDeleted
        }
      });
      setPaginationData(response.data);
    } catch (error) {
      toast.error(error.message || "Failed to load roles.");
    }
  }, [api, page, pageSize, searchTerm, showDeleted, setPaginationData]);

  // Load permissions for a role (only return what is explicitly defined)
  const loadPermissions = (role) => {
    if (!role || !role.permissions) return {};

    let rolePerms = role.permissions;
    if (typeof rolePerms === "string") {
      try {
        rolePerms = JSON.parse(rolePerms);
      } catch (e) {
        console.error("Failed to parse role permissions", e);
        return {};
      }
    }

    return { ...rolePerms };
  };

  useEffect(() => {
    loadRoles();
    if (isSuperAdmin) {
      api
        .get("/tenants")
        .then((res) => setTenants(res.data))
        .catch((err) => console.error(err));
    }
  }, [loadRoles, isSuperAdmin, api]);

  const handleOpenDialog = (role = null) => {
    setEditingRole(role);
    setRoleName(role ? role.name : "");
    setSelectedTenantId(role ? role.tenantId : "");
    setIsDialogOpen(true);
  };

  // Permissions dialog handlers
  const handleOpenPermDialog = (role) => {
    setPermRole(role);
    setPermissions(loadPermissions(role));
    setIsPermDialogOpen(true);
  };

  const handleClosePermDialog = () => {
    setIsPermDialogOpen(false);
    setPermRole(null);
    setPermissions({});
  };

  const handlePermChange = (screenId, permType) => {
    const canEdit = isSuperAdmin || !!user?.permissions?.[screenId]?.[permType];
    if (!canEdit) return;

    setPermissions((prev) => {
      const current = prev[screenId] || getSingleScreenDefault();
      return {
        ...prev,
        [screenId]: {
          ...current,
          [permType]: !current[permType]
        }
      };
    });
  };

  const handleSelectAllColumn = (permType, checked) => {
    setPermissions((prev) => {
      const newPerms = { ...prev };
      availableScreens.forEach((screen) => {
        const canEdit = isSuperAdmin || !!user?.permissions?.[screen.id]?.[permType];
        if (canEdit && (newPerms[screen.id] || checked)) {
          newPerms[screen.id] = {
            ...(newPerms[screen.id] || getSingleScreenDefault()),
            [permType]: checked
          };
        }
      });
      return newPerms;
    });
  };

  const handleSavePermissions = async () => {
    if (!permRole) return;
    try {
      // Prune permissions: only send if it has at least one true value
      // OR if it existed in the original role (to allow setting back to false)
      const originalPerms = loadPermissions(permRole);
      const prunedPermissions = Object.keys(permissions).reduce((acc, screenId) => {
        const perms = permissions[screenId];
        const hasAnyTrue = Object.values(perms).some((v) => v === true);
        const existedOriginally = !!originalPerms[screenId];

        if (hasAnyTrue || existedOriginally) {
          acc[screenId] = perms;
        }
        return acc;
      }, {});

      // Send only the pruned permissions object
      await api.put(`/roles/${permRole.id}`, {
        permissions: prunedPermissions
      });
      toast.success("Permissions updated");
      handleClosePermDialog();
      loadRoles();
    } catch (error) {
      toast.error(error.message || "Failed to update permissions");
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingRole(null);
    setRoleName("");
    setSelectedTenantId("");
  };

  const handleSaveRole = async (e) => {
    e.preventDefault();
    if (isSuperAdmin && !selectedTenantId) {
      toast.error("Please select a tenant for the role");
      return;
    }
    try {
      if (editingRole) {
        await api.put(`/roles/${editingRole.id}`, {
          name: roleName,
          tenantId: selectedTenantId || undefined
        });
        toast.success("Role updated");
      } else {
        await api.post(`/roles`, {
          name: roleName,
          tenantId: selectedTenantId || undefined
        });
        toast.success("Role created");
      }
      handleCloseDialog();
      loadRoles();
    } catch (error) {
      toast.error(error.message || "Failed to save role");
    }
  };

  const confirm = useConfirm();

  const handleDeleteRole = async (roleId) => {
    const isConfirmed = await confirm({
      title: "Delete Role",
      message: "Are you sure you want to delete this role?"
    });
    if (!isConfirmed) return;
    try {
      await api.delete(`/roles/${roleId}`);
      toast.success("Role deleted");
      loadRoles();
    } catch (error) {
      toast.error(error.message || "Failed to delete role");
    }
  };

  return (
    <div className="flex-1 flex flex-col animate-in fade-in duration-500">
      <div className="flex-1 space-y-6">
        <PageHeader
          title="Role Management"
          subtitle="Configure system roles and screen-level permissions"
          actionLabel={user?.permissions?.role?.create ? "Add Role" : null}
          onActionClick={() => handleOpenDialog()}
          icon={ShieldCheck}
          pageIcon={ShieldCheck}
          extraActions={
            <SearchInput
              placeholder="Search roles..."
              className="md:w-72"
              onSearch={(val) => {
                setSearchTerm(val);
                setPage(0);
              }}
            />
          }
        />
        <RoleTable
          roles={filteredRoles}
          permissions={{
            canUpdate: user?.permissions?.role?.update,
            canDelete: user?.permissions?.role?.delete
          }}
          onEdit={handleOpenDialog}
          onDelete={handleDeleteRole}
          onConfigurePermissions={handleOpenPermDialog}
          showDeleted={showDeleted}
          setShowDeleted={setShowDeleted}
          setCurrentPage={setPage}
        />
        {/* Permissions Dialog */}
        <Dialog open={isPermDialogOpen} onOpenChange={setIsPermDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Screen Permissions for {permRole?.name}</DialogTitle>
              <DialogDescription>Set create, read, update, delete permissions for each screen.</DialogDescription>
            </DialogHeader>
            <PermissionTable
              screens={availableScreens}
              permissions={permissions}
              onPermissionChange={handlePermChange}
              onSelectAllColumn={handleSelectAllColumn}
              defaultPermissions={{}}
              userPermissions={user?.permissions}
              isSuperAdmin={isSuperAdmin}
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={handleClosePermDialog}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSavePermissions}>
                Save Permissions
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8">
        <Pagination pagination={pagination} />
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRole ? "Edit Role" : "Add Role"}</DialogTitle>
            <DialogDescription>{editingRole ? "Update the role name." : "Create a new role."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveRole} className="space-y-4">
            <div>
              <Label htmlFor="roleName">Role Name</Label>
              <Input
                id="roleName"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                required
                placeholder="Enter role name"
              />
            </div>

            {isSuperAdmin && (
              <div className="space-y-2">
                <Label htmlFor="tenantId">
                  Assign to Tenant <span className="text-red-500">*</span>
                </Label>
                <LazySearchSelect
                  value={selectedTenantId}
                  onChange={(val) => setSelectedTenantId(val)}
                  apiUrl="/tenants"
                  placeholder="Select a tenant"
                  searchPlaceholder="Search tenants..."
                  labelKey="name"
                  valueKey="id"
                  disableInitialFetch={true}
                  renderItem={(t) => (
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium truncate">{t.name}</span>
                      <span className="text-[10px] text-slate-500 font-mono truncate">{t.tenantKey}</span>
                    </div>
                  )}
                />
                <p className="text-xs text-slate-500">Select which tenant this role belongs to.</p>
              </div>
            )}

            <Button type="submit" variant="primary" className="w-full">
              {editingRole ? "Update Role" : "Create Role"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoleManagement;
